import ContextMenu from '@basic/ContextMenu';
import { setupForDark } from '@theme';
import { useMediaQuery } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { Sheet } from 'react-modal-sheet';
import Modal from './Modal';
import SectionTitle from './SectionTitle';

export declare type SheetDetent = 'full' | 'content' | 'default';
export declare type DisplayMode = 'context-menu' | 'modal' | 'none';

interface BottomSheetProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  title?: string;
  isOpen: boolean;
  onClose: () => void;
  detent?: SheetDetent;
  desktopVersion?: DisplayMode;
  desktopMenuPosition?: { x: number; y: number };
}

const BottomSheet = ({
  children,
  title = '',
  isOpen = false,
  onClose = () => {},
  detent = 'content',
  desktopVersion = 'context-menu',
  desktopMenuPosition,
  ...props
}: BottomSheetProps) => {
  const isSmOrHigher = useMediaQuery('(max-width: 640px)');
  const [innerIsOpen, setInnerIsOpen] = useState(false);
  const isMobile = useMemo(() => {
    return isSmOrHigher;
  }, [isSmOrHigher]);

  // desktopVersion 'none' means the caller shows its own desktop UI – close so the caller's state stays clean
  const suppressedOnDesktop = !isMobile && isOpen && desktopVersion === 'none';

  useEffect(() => {
    if (suppressedOnDesktop) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppressedOnDesktop]);

  useEffect(() => {
    // This is just necessary because of the bug in the lib that is used by BottomSheet
    // When this component renders the change in the selected child it causes the sheet to
    // rerender and we close it in meantime so the sheet is trying to update nonexisting component
    // By using setTimeout we are delaying the closing of the sheet to the next tick
    if (isOpen === innerIsOpen) {
      return;
    }
    if (isMobile) {
      setTimeout(() => setInnerIsOpen(isOpen), 0);
    } else {
      setInnerIsOpen(isOpen);
    }
  }, [isOpen, isMobile]);

  const innerOnClose = () => {
    setInnerIsOpen(false);
    onClose();
  };

  if (suppressedOnDesktop) {
    return <></>;
  }

  return (
    <>
      {isMobile && (
        <Sheet isOpen={innerIsOpen} onClose={innerOnClose} detent={detent}>
          <Sheet.Container
            style={{
              borderTopLeftRadius: '25px',
              borderTopRightRadius: '25px'
            }}
          >
            <Sheet.Header
              className={classNames('rounded-t-lg', setupForDark('bg-modal-primary'))}
            />
            <Sheet.Content className={classNames('pb-xxl', setupForDark('bg-modal-primary'))}>
              {title && <SectionTitle text={title} center={true} className='p-xs' />}
              {children}
            </Sheet.Content>
          </Sheet.Container>
          <Sheet.Backdrop onTap={innerOnClose} data-testid='bottom-sheet-backdrop' />
        </Sheet>
      )}

      {!isMobile && innerIsOpen && desktopVersion === 'context-menu' && (
        <ContextMenu onClose={innerOnClose} withBackground={false} position={desktopMenuPosition}>
          {children}
        </ContextMenu>
      )}
      {!isMobile && innerIsOpen && desktopVersion === 'modal' && (
        <Modal isOpen={innerIsOpen} onClose={innerOnClose}>
          <div {...props}>{children}</div>
        </Modal>
      )}
    </>
  );
};
export default BottomSheet;
